import {ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit, output} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ProjectService, ProjectTypeMapping} from '../../services/project.service';
import {Subscription} from "rxjs";
import {NgIf} from '@angular/common';
import {NgMultiLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent} from '@ng-select/ng-select';


export interface GroupProjectType {
  id: string;
  name: string;
  type: 'group' | 'option'
  items?: ProjectTypeMapping[];

}

@Component({
  selector: 'app-multi-project-type-selector',
  templateUrl: './multi-project-type-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgMultiLabelTemplateDirective,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    NgSelectComponent
  ]
})
export class MultiProjectTypeSelectorComponent implements OnInit, OnDestroy {
  private readonly projectService: ProjectService = inject(ProjectService);
  readonly selectedTypes = input<string[]>([]);
  readonly typesSelected = output<ProjectTypeMapping[]>();

  getProjectTypeSubscription: Subscription = new Subscription;
  allSelected = false;
  projectTypes: ProjectTypeMapping[] = [];
  formControl = new FormControl<string[]>([]);
  groupedProjectTypes: GroupProjectType[] = [];
  placeholder = 'Select Project Types...';

  constructor() {
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((selectedIds) => {
        if (!selectedIds || selectedIds.length === 0) {
          this.typesSelected.emit([]);
          return;
        }

        const selectedProjectTypes = this.projectTypes.filter((projectType) => selectedIds.includes(projectType.id));

        if (selectedProjectTypes.length > 0) {
          this.typesSelected.emit(selectedProjectTypes);
        }
      })
  }

  ngOnInit() {
    this.getProjectTypeSubscription = this.projectService.getProjectTypes().subscribe({
      next: (projectTypes) => {
        this.projectTypes = projectTypes;
        this.buildGroupedProjectTypes(projectTypes);

        if (this.selectedTypes().length > 0 && projectTypes.length > 0) {
          const validIds = this.selectedTypes()
            .map((name) => projectTypes.find((pT) => pT.name === name)?.id)
            .filter((id): id is string => id !== undefined);

          if (validIds.length > 0) {
            this.formControl.patchValue(validIds, {emitEvent: false});
            this.updateAllSelectedFlag();
          }
        }
      },
      error: (error) => {
        console.error('Error fetching project types:', error);
      },
    })
  }

  ngOnDestroy(): void {
    this.getProjectTypeSubscription.unsubscribe();
  }

  onBlur(): void {
    this.formControl.markAsTouched();
  }

  onSelectionChange(_: unknown): void {
    this.updateAllSelectedFlag();
  }

  clearAll(): void {
    this.formControl.patchValue([]);
    this.typesSelected.emit([]);
    this.allSelected = false;
  }

  isSelected(type: string): boolean {
    return this.selectedTypes().includes(type);
  }

  private buildGroupedProjectTypes(projectTypes: ProjectTypeMapping[]) {
    this.groupedProjectTypes = projectTypes.map((projectType) => ({
      id: projectType.id,
      name: projectType.name,
      type: 'option' as const,
    }))
  }

  private updateAllSelectedFlag(): void {
    const selectedCount = this.formControl.value?.length || 0;
    const totalOptions = this.groupedProjectTypes.filter((g) => g.type === 'option').length;
    this.allSelected = selectedCount > 0 && selectedCount === totalOptions;
  }
}
